/* eslint-disable @angular-eslint/no-input-rename */
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { AssetResource } from '@models/Core/Asset';
import { AssetsPipe } from '@pipes/assets.pipe';
import { FormatSecondsToMinutesPipe } from '@pipes/time/format-seconds-to-minutes.pipe';
import clamp from 'lodash/clamp';
import { Button, ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { SliderModule } from 'primeng/slider';
import { TooltipModule } from 'primeng/tooltip';
import { BehaviorSubject } from 'rxjs';

import { VideoProjectFormService } from '../video-project.form';

@Component({
  selector: 'app-video-asset-resource',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SliderModule,
    ButtonModule,
    TooltipModule,
    MessageModule,
    AssetsPipe,
    Button,
    FormatSecondsToMinutesPipe,
  ],
  templateUrl: './video-asset-resource.component.html',
  styleUrl: './video-asset-resource.component.scss',
})
export class VideoAssetResourceComponent implements OnInit, OnChanges {
  @ViewChild('media') media: ElementRef<HTMLVideoElement>;

  @Input() id?: string;
  @Input() resource?: AssetResource;
  @Input() controls = false;
  @Input() playOnHover = false;
  /**
   * @description playback start time in milliseconds
   * @default -1 playback from beginning
   */
  @Input('startAt') private _startAt = -1;
  /**
   * @description playback end time in milliseconds
   * @default -1 playback full duration
   */
  @Input('endAt') private _endAt = -1;

  isPlaying = false;
  isMuted = false;

  currentTime = 0;
  duration = 0;

  get startAt() {
    const seconds = this._startAt / 1000;
    return clamp(seconds, 0, this.duration);
  }

  get endAt() {
    if (this._endAt < 0) {
      return this.duration;
    }
    const seconds = this._endAt / 1000;
    return clamp(seconds, this.startAt, this.duration);
  }

  get progress() {
    const current = this.currentTime - this.startAt;
    const total = this.endAt - this.startAt;
    return total > 0 ? (current / total) * 100 : 0;
  }

  set progress(value: number) {
    this.seek(value);
  }

  private readonly progressSubject = new BehaviorSubject<number>(0);

  readonly $onProgressChanges = this.progressSubject.asObservable();

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly videoProjectFormService: VideoProjectFormService,
  ) {}

  ngOnInit(): void {
    this.videoProjectFormService
      .$onActiveResourceChanges(active => active !== this)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.isPlaying) {
          this.stop();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes._startAt && !changes._startAt.isFirstChange()) {
      this.onAfterStartAtChanges(changes._startAt.currentValue);
    }
    if (changes._endAt && !changes._endAt.isFirstChange()) {
      this.onAfterEndAtChanges(changes._endAt.currentValue);
    }
  }

  private onAfterStartAtChanges(value: number) {
    if (!Number.isFinite(value)) {
      return;
    }
    const seconds = value / 1000;
    this.media.nativeElement.currentTime = this.currentTime = clamp(
      seconds,
      0,
      this.duration,
    );
  }

  private onAfterEndAtChanges(value: number) {
    if (!Number.isFinite(value)) {
      return;
    }
    if (value < 0) {
      this.media.nativeElement.currentTime = this.currentTime = this.duration;
    } else {
      const seconds = value / 1000;
      this.media.nativeElement.currentTime = this.currentTime = clamp(
        seconds,
        this.startAt,
        this.duration,
      );
    }
  }

  onLoadedMetadata() {
    this.duration = this.media.nativeElement.duration;
    this.onAfterStartAtChanges(this._startAt);
  }

  onTimeUpdate() {
    this.currentTime = Math.min(
      this.media.nativeElement.currentTime,
      this.endAt,
    );
    this.progressSubject.next(this.progress);
    if (this.currentTime >= this.endAt) {
      this.pause();
      this.media.nativeElement.dispatchEvent(
        new Event('ended', { bubbles: true }),
      );
      return;
    }
  }

  onPlay() {
    this.videoProjectFormService.setActiveResource(this);
    this.isPlaying = true;
  }

  onPause() {
    this.isPlaying = false;
  }

  onEnd() {
    this.isPlaying = false;
    this.videoProjectFormService.setActiveResource(undefined);
  }

  play() {
    if (this.currentTime >= this.endAt) {
      this.media.nativeElement.currentTime = this.currentTime = this.startAt;
    }
    this.media.nativeElement.play();
  }

  pause() {
    this.media.nativeElement.pause();
  }

  stop() {
    this.pause();
    this.media.nativeElement.currentTime = this.currentTime = this.startAt;
  }

  mute() {
    this.media.nativeElement.volume = 0;
    this.isMuted = true;
  }

  unmute() {
    this.media.nativeElement.volume = 1;
    this.isMuted = false;
  }

  togglePlayback() {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  toggleVolume() {
    if (this.isMuted) {
      this.unmute();
    } else {
      this.mute();
    }
  }

  seek(value: number) {
    const progress = clamp(value, 0, 100);
    const duration = this.endAt - this.startAt;
    this.media.nativeElement.currentTime =
      this.startAt + (progress / 100) * duration;
  }
}
